import React, {
  useEffect, useState,
} from 'react';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useNavigate } from 'react-router';

import { Button } from '../../../components/DetailsModal';
import EventCell from '../../../components/EventCell';
import SkeletonStack from '../../../components/SkeletonStack';
import WeekCalendar from '../../../components/WeekCalendar';
import { CalendarEntry } from '../../../components/WeekCalendar/types';
import { getCurrentUserId } from '../../../services/auth';
import {
  useGetStudentLessonEnrolmentsQuery, useLazyGetLessonQuery,
} from '../../../services/classes';
import { LessonEnrolment } from '../../../services/classes/types';
import { useGetCoursesQuery } from '../../../services/courses';
import { useGetStudentQuery } from '../../../services/students';
import { BaseTutorial } from '../../../services/students/types';
import { useLazyGetTutorialQuery, useUnbookTutorialMutation } from '../../../services/tutorials';
import {
  getBaseAttributes,
} from '../../../services/tutorials/utils';
import addDays from '../../../utils/common/addDays';
import {
  getLessonEnrolmentClass,
  getLessonEnrolmentCourse,
  getLessonEnrolmentLessonId,
  getLessonEnrolmentShortLabel,
  getLessonEnrolmentSubject,
} from '../../../utils/common/classes';
import { getColorKeyFromCourse } from '../../../utils/common/courses';
import useError from '../../../utils/react/hooks/useError';
import useSuccess from '../../../utils/react/hooks/useSuccess';
import { getStudentTutorialState, TutorialUserType } from '../../TutorialsPage/StudentTutorialsPage/utils';
import { EventDetails } from '../types';
import { getStudentLessonState } from '../utils';

const TODAY = dayjs().startOf('day');

const MIN_DISPLAY_DATE = addDays(TODAY, -2 * 7);
const MAX_DISPLAY_DATE = addDays(TODAY, 4 * 7);

const StudentTutorialsCalendar = () => {
  const studentId = getCurrentUserId();
  const navigate = useNavigate();

  const { data: courses } = useGetCoursesQuery({});
  const { data: student, isLoading } = useGetStudentQuery({ studentId: studentId ?? '' });
  const { data: lessonEnrolments } = useGetStudentLessonEnrolmentsQuery({
    studentId: studentId ?? '',
  }, { skip: !studentId });

  const [tutorialEntries, setTutorialEntries] = useState<CalendarEntry<EventDetails>[]>([]);
  const [lessonEntries, setLessonEntries] = useState<CalendarEntry<EventDetails>[]>([]);

  const [unbookTutorial,
    { isError: isUnbookingError,
      isSuccess: isUnbookingSuccess,
      error: unbookingError,
      isLoading: isUnbookingLoading }] = useUnbookTutorialMutation();

  const [fetchTutorialProperties] = useLazyGetTutorialQuery();
  const [fetchLessonProperties] = useLazyGetLessonQuery();

  const getTutorialProperties = async (course: string, startDatetimeIdentifier: string):
  Promise<any> => {
    let details = {};
    await fetchTutorialProperties({
      course,
      id: startDatetimeIdentifier,
    })
      .unwrap()
      .then((tutorial) => {
        details = {
          values: _.pick(tutorial?.data, ['host', 'description', 'joinLink']),
          fields: _.pick(tutorial?.metadata, ['host', 'description', 'joinLink']),
        };
      });
    return details;
  };

  const getLessonProperties = async (subject: string, classId: string, lesson: string):
  Promise<any> => {
    let details = {};
    await fetchLessonProperties({
      subject,
      classId,
      lesson,
    })
      .unwrap()
      .then((lessonData) => {
        details = {
          values: _.pick(lessonData?.data, ['teacher']),
          fields: _.pick(lessonData?.metadata, ['teacher']),
        };
      });
    return details;
  };

  // to calendar entry for tutorials
  const toCalendarEntryTutorial = (tutorial: BaseTutorial): CalendarEntry<EventDetails> => {
    const unbookButton: Button = {
      fn: () => unbookTutorial({
        course: tutorial.course,
        id: tutorial.startDatetimeIdentifier,
        studentId,
      }),
      label: 'Unbook',
      color: 'error',
      loading: isUnbookingLoading,
    };

    const joinButton: Button = {
      fn: async () => {
        const tutorialProperties = await getTutorialProperties(
          tutorial.course, tutorial.startDatetimeIdentifier);
        window.open(tutorialProperties.joinLink.value);
      },
      label: 'Join',
      color: 'success',
    };

    // They shouldn't be able to book tutorials on home page
    const bookButton: Button = {
      fn: () => {},
      label: 'Book',
      disabled: true,
    };

    const baseAttributes = getBaseAttributes(tutorial, courses?.data ?? []);

    return {
      ...baseAttributes,
      details: {
        ...baseAttributes.details,
        state: getStudentTutorialState(
          tutorial, TutorialUserType.Attendee, bookButton, unbookButton, joinButton,
        ),
        properties: {},
        fetchProperties: async () =>
          getTutorialProperties(tutorial.course, tutorial.startDatetimeIdentifier),
      },
    };
  };

  const toCalendarEntryLesson = (lesson: LessonEnrolment): CalendarEntry<EventDetails> => {
    const course = getLessonEnrolmentCourse(lesson);
    const label = getLessonEnrolmentShortLabel(lesson, courses?.data ?? []);
    const color = getColorKeyFromCourse(course, courses?.data ?? []);
    const subject = getLessonEnrolmentSubject(lesson);
    const classId = getLessonEnrolmentClass(lesson);
    const lessonId = getLessonEnrolmentLessonId(lesson);

    const makeUpButton: Button = {
      fn: () => navigate(`/student/${subject}/${classId}/${encodeURIComponent(lessonId)}/reschedule`),
      label: 'Schedule Makeup',
      color: 'success',
    };

    return {
      id: lesson.id,
      startDatetime: lesson.startDatetime,
      details: {
        title: label,
        color,
        state: getStudentLessonState(lesson, makeUpButton),
        properties: {},
        fetchProperties: async () =>
          getLessonProperties(subject, classId, lessonId),
      },
    };
  };

  useError(isUnbookingError, unbookingError);
  useSuccess(isUnbookingSuccess, 'Tutorial Unbooked');

  useEffect(() => {
    if (student && courses) {
      const tutorials: CalendarEntry<EventDetails>[] = student?.data.tutorials
        ?.map((tutorial: BaseTutorial) =>
          toCalendarEntryTutorial(tutorial)) || [];

      setTutorialEntries(tutorials);
    }
  }, [student, courses]);

  useEffect(() => {
    if (courses && lessonEnrolments) {
      const lessons : CalendarEntry<EventDetails>[] = lessonEnrolments?.data
        ?.map((lesson: LessonEnrolment) =>
          toCalendarEntryLesson(lesson)) || [];

      setLessonEntries(lessons);
    }
  }, [courses, lessonEnrolments]);

  return (
    <>
      {isUnbookingLoading && (
        <Backdrop open>
          <CircularProgress />
        </Backdrop>
      )}
      {
        isLoading ? (
          <SkeletonStack />
        ) : (
          <WeekCalendar
            renderContent={(entry) => (
              <EventCell
                key={entry.id}
                startDatetime={entry.startDatetime}
                duration={entry.details.duration}
                title={entry.details.title}
                color={entry.details.color}
                state={entry.details.state}
                fetchProperties={entry.details.fetchProperties}
                showStatus
              />
            )}
            data={[...tutorialEntries, ...lessonEntries]}
            minDate={MIN_DISPLAY_DATE}
            maxDate={MAX_DISPLAY_DATE}
          />
        )
      }
    </>
  );
};
export default StudentTutorialsCalendar;
